import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Palette, Upload, X, RotateCcw, Lock, Unlock } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  useStandardTemplate,
  useUpdateStandardTemplateSettings,
  useUploadTemplateLogo,
  useResetStandardTemplate,
} from "@/features/standard-template/hooks/use-standard-template";
import { useCompanyDetails } from "@/features/company-details/hooks/use-company-details";
import { TemplatePreview } from "@/features/standard-template/components/template-preview";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/(app)/dashboard/template-customizer/")({
  component: TemplateCustomizerPage,
});

function TemplateCustomizerPage() {
  const { t } = useTranslation();
  useDocumentTitle(t("templateCustomizer.title"));
  const { toast } = useToast();

  const { data: template, isLoading, error } = useStandardTemplate();
  const { data: companyDetails } = useCompanyDetails();
  const updateSettings = useUpdateStandardTemplateSettings();
  const uploadLogo = useUploadTemplateLogo();
  const resetTemplate = useResetStandardTemplate();

  // Debug: Log template margins
  useEffect(() => {
    if (template) {
      console.log('Template margins:', {
        leftMargin: template.leftMargin,
        rightMargin: template.rightMargin,
        topMargin: template.topMargin,
        bottomMargin: template.bottomMargin,
      });
    }
  }, [template]);

  const [fontSize, setFontSize] = useState(12);
  const [fontColor, setFontColor] = useState("#000000");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoWidth, setLogoWidth] = useState(200);
  const [logoHeight, setLogoHeight] = useState(80);
  const [aspectRatioLocked, setAspectRatioLocked] = useState(true);
  const [originalAspectRatio, setOriginalAspectRatio] = useState(200 / 80);
  const [tableColumns, setTableColumns] = useState({
    position: true,
    description: true,
    productType: false,
    itemDate: false,
    unit: true,
    quantity: true,
    unitPrice: true,
    total: true,
  });

  const [invoiceInfoBlockRows, setInvoiceInfoBlockRows] = useState({
    invoiceNumber: true,
    invoiceDate: true,
    paymentDueDate: true,
    vatId: true,
    customerNumber: true,
    pageNumber: true,
  });

  useEffect(() => {
    if (template) {
      setFontSize(template.fontSize || 12);
      const rootContainer = (template as any).rootContainer || {};
      setFontColor(rootContainer.accentColor || "#000000");
      setLogoUrl(template.logoUrl || null);
      setLogoWidth((template as any).logoWidth || 200);
      setLogoHeight((template as any).logoHeight || 80);
      
      // Table columns from template metadata if available
      if ((template as any).tableColumns) {
        setTableColumns({
          position: (template as any).tableColumns.position ?? true,
          description: (template as any).tableColumns.description ?? true,
          productType: (template as any).tableColumns.productType ?? false,
          itemDate: (template as any).tableColumns.itemDate ?? false,
          unit: (template as any).tableColumns.unit ?? true,
          quantity: (template as any).tableColumns.quantity ?? true,
          unitPrice: (template as any).tableColumns.unitPrice ?? true,
          total: (template as any).tableColumns.total ?? true,
        });
      }

      // Invoice info block rows from template metadata if available
      if ((template as any).invoiceInfoBlockRows) {
        setInvoiceInfoBlockRows({
          invoiceNumber: (template as any).invoiceInfoBlockRows.invoiceNumber ?? true,
          invoiceDate: (template as any).invoiceInfoBlockRows.invoiceDate ?? true,
          paymentDueDate: (template as any).invoiceInfoBlockRows.paymentDueDate ?? true,
          vatId: (template as any).invoiceInfoBlockRows.vatId ?? true,
          customerNumber: (template as any).invoiceInfoBlockRows.customerNumber ?? true,
          pageNumber: (template as any).invoiceInfoBlockRows.pageNumber ?? true,
        });
      }
    }
  }, [template]);

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({
        accentColor: fontColor,
        logoUrl,
        logoWidth,
        logoHeight,
        tableColumns,
        invoiceInfoBlockRows,
      });
      
      toast({
        title: t("templateCustomizer.saved"),
        description: t("common.success"),
      });
    } catch (error) {
      toast({
        title: t("templateCustomizer.error"),
        description: String(error),
        variant: "destructive",
      });
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const result = await uploadLogo.mutateAsync(file);
        
        // Load image to get original dimensions
        const img = new Image();
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          setOriginalAspectRatio(aspectRatio);
          setLogoWidth(Math.min(img.width, 400));
          setLogoHeight(Math.min(img.height, 200));
        };
        img.src = result.logoUrl;
        
        setLogoUrl(result.logoUrl);
        toast({
          title: t("common.success"),
          description: t("templateCustomizer.logo.upload"),
        });
      } catch (error) {
        toast({
          title: t("templateCustomizer.error"),
          description: String(error),
          variant: "destructive",
        });
      }
    }
  };

  const handleLogoWidthChange = (newWidth: number) => {
    setLogoWidth(newWidth);
    if (aspectRatioLocked && originalAspectRatio) {
      setLogoHeight(Math.round(newWidth / originalAspectRatio));
    }
  };

  const handleLogoHeightChange = (newHeight: number) => {
    setLogoHeight(newHeight);
    if (aspectRatioLocked && originalAspectRatio) {
      setLogoWidth(Math.round(newHeight * originalAspectRatio));
    }
  };

  const handleReset = async () => {
    try {
      await resetTemplate.mutateAsync();
      toast({
        title: t("common.success"),
        description: t("templateCustomizer.reset"),
      });
    } catch (error) {
      toast({
        title: t("templateCustomizer.error"),
        description: String(error),
        variant: "destructive",
      });
    }
  };

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-red-500">Fehler beim Laden: {String(error)}</p>
          <Button onClick={() => window.location.reload()}>Neu laden</Button>
        </div>
      </div>
    );
  }

  if (isLoading || !template) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center h-64">
          <p>{t("common.loading")}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Palette className="h-10 w-10 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">{t("templateCustomizer.title")}</h1>
          <p className="text-muted-foreground">{t("templateCustomizer.description")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("templateCustomizer.settings")}</CardTitle>
              <CardDescription>{t("templateCustomizer.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{t("templateCustomizer.logo.title")}</Label>
                {logoUrl ? (
                  <div className="relative">
                    <img src={logoUrl} alt="Logo" className="w-full h-24 object-contain border rounded-md" />
                    <Button variant="destructive" size="sm" className="absolute top-2 right-2" onClick={() => setLogoUrl(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" />
                    <Label htmlFor="logo-upload" className="flex items-center justify-center gap-2 border-2 border-dashed rounded-md p-6 cursor-pointer hover:bg-muted transition-colors">
                      <Upload className="h-5 w-5" />
                      <span>{t("templateCustomizer.logo.upload")}</span>
                    </Label>
                  </div>
                )}
                {logoUrl && (
                  <div className="space-y-2 mt-2">
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label htmlFor="logo-width" className="text-xs">{t("templateCustomizer.logo.width")}</Label>
                        <Input
                          id="logo-width"
                          type="number"
                          min="50"
                          max="800"
                          value={logoWidth}
                          onChange={(e) => handleLogoWidthChange(Number(e.target.value))}
                          className="h-8"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => setAspectRatioLocked(!aspectRatioLocked)}
                      >
                        {aspectRatioLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                      </Button>
                      <div className="flex-1">
                        <Label htmlFor="logo-height" className="text-xs">{t("templateCustomizer.logo.height")}</Label>
                        <Input
                          id="logo-height"
                          type="number"
                          min="20"
                          max="400"
                          value={logoHeight}
                          onChange={(e) => handleLogoHeightChange(Number(e.target.value))}
                          className="h-8"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">{t("templateCustomizer.general.title")}</h3>
                <div className="space-y-2">
                  <Label htmlFor="fontColor">Farbe</Label>
                  <div className="flex gap-2">
                    <Input id="fontColor" type="color" value={fontColor} onChange={(e) => setFontColor(e.target.value)} className="w-16 h-10 p-1" />
                    <Input type="text" value={fontColor} onChange={(e) => setFontColor(e.target.value)} className="flex-1" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ändert: "RECHNUNG" Text, Tabellenlinien und "Gesamtsumme"
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Tabellenspalten</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-position"
                      checked={tableColumns.position}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, position: checked })}
                    />
                    <Label htmlFor="col-position" className="cursor-pointer">Position</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-description"
                      checked={tableColumns.description}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, description: checked })}
                    />
                    <Label htmlFor="col-description" className="cursor-pointer">Beschreibung</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-quantity"
                      checked={tableColumns.quantity}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, quantity: checked })}
                    />
                    <Label htmlFor="col-quantity" className="cursor-pointer">Menge</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-unit"
                      checked={tableColumns.unit}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, unit: checked })}
                    />
                    <Label htmlFor="col-unit" className="cursor-pointer">Einheit</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-unitPrice"
                      checked={tableColumns.unitPrice}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, unitPrice: checked })}
                    />
                    <Label htmlFor="col-unitPrice" className="cursor-pointer">Einzelpreis</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-total"
                      checked={tableColumns.total}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, total: checked })}
                    />
                    <Label htmlFor="col-total" className="cursor-pointer">Gesamt</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-productType"
                      checked={tableColumns.productType}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, productType: checked })}
                    />
                    <Label htmlFor="col-productType" className="cursor-pointer">Produkttyp</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="col-itemDate"
                      checked={tableColumns.itemDate}
                      onCheckedChange={(checked) => setTableColumns({ ...tableColumns, itemDate: checked })}
                    />
                    <Label htmlFor="col-itemDate" className="cursor-pointer">Datum</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold">Rechnungsinfo-Block</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="infoblock-invoiceNumber"
                      checked={invoiceInfoBlockRows.invoiceNumber}
                      onCheckedChange={(checked) => setInvoiceInfoBlockRows({ ...invoiceInfoBlockRows, invoiceNumber: checked })}
                    />
                    <Label htmlFor="infoblock-invoiceNumber" className="cursor-pointer">Rechnungsnummer</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="infoblock-invoiceDate"
                      checked={invoiceInfoBlockRows.invoiceDate}
                      onCheckedChange={(checked) => setInvoiceInfoBlockRows({ ...invoiceInfoBlockRows, invoiceDate: checked })}
                    />
                    <Label htmlFor="infoblock-invoiceDate" className="cursor-pointer">Rechnungsdatum</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="infoblock-paymentDueDate"
                      checked={invoiceInfoBlockRows.paymentDueDate}
                      onCheckedChange={(checked) => setInvoiceInfoBlockRows({ ...invoiceInfoBlockRows, paymentDueDate: checked })}
                    />
                    <Label htmlFor="infoblock-paymentDueDate" className="cursor-pointer">Zahlungsbedingungen</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="infoblock-vatId"
                      checked={invoiceInfoBlockRows.vatId}
                      onCheckedChange={(checked) => setInvoiceInfoBlockRows({ ...invoiceInfoBlockRows, vatId: checked })}
                    />
                    <Label htmlFor="infoblock-vatId" className="cursor-pointer">Ust.-IdNr.</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="infoblock-customerNumber"
                      checked={invoiceInfoBlockRows.customerNumber}
                      onCheckedChange={(checked) => setInvoiceInfoBlockRows({ ...invoiceInfoBlockRows, customerNumber: checked })}
                    />
                    <Label htmlFor="infoblock-customerNumber" className="cursor-pointer">Kundennummer</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="infoblock-pageNumber"
                      checked={invoiceInfoBlockRows.pageNumber}
                      onCheckedChange={(checked) => setInvoiceInfoBlockRows({ ...invoiceInfoBlockRows, pageNumber: checked })}
                    />
                    <Label htmlFor="infoblock-pageNumber" className="cursor-pointer">Seite</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Button className="w-full" onClick={handleSave} disabled={updateSettings.isPending}>
                  {updateSettings.isPending ? t("templateCustomizer.saving") : t("templateCustomizer.save")}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full" disabled={resetTemplate.isPending}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      {t("templateCustomizer.reset")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("templateCustomizer.reset")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("templateCustomizer.resetConfirm")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={handleReset}>{t("common.confirm")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>{t("templateCustomizer.preview")}</CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100vh-12rem)] overflow-auto">
              <div className="bg-muted/30 rounded-lg p-8 min-h-full flex items-start justify-center">
                <TemplatePreview
                  template={template}
                  previewFontSize={fontSize}
                  previewAccentColor={fontColor}
                  previewLogoUrl={logoUrl}
                  logoWidth={logoWidth}
                  logoHeight={logoHeight}
                  tableColumns={tableColumns}
                  companyDetails={companyDetails}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}